import { Property } from '@square/ignition';

interface SortAndFilterOptions {
  typeFieldValue?: string | undefined;
}

// Catalog uses _data, wheres terminal uses _options
const typeDataFieldSuffixes = ['_data', '_options'];

function isTypeDataFieldForType(propertyName: string, type: string): boolean {
  return typeDataFieldSuffixes.some(
    (suffix) => propertyName === `${type.toLowerCase()}${suffix}`
  );
}

function isAnyTypeDataField(propertyName: string): boolean {
  return typeDataFieldSuffixes.some((suffix) => propertyName.endsWith(suffix));
}

const MAX_DEPTH_PROPERTY_VALIDATION = 1;
/**
 * Check to see if the object property has no valid child properties. What can happen is that an entity
 * has only properties that are readonly. That means, in the API Explorer, there's no fields to render.
 * @param property The property to check
 * @returns True if the property is empty, false otherwise
 */
function isApiExplorerEntityPropertyEmpty(
  property: Property,
  depth: number
): boolean {
  // We only check to a certain depth, otherwise we could be recursively checking forever due to cycles
  if (depth > MAX_DEPTH_PROPERTY_VALIDATION) {
    return false;
  }

  // Skip enums, as they don't have children
  if (property.isEnum || property.containsEnum) {
    return false;
  }

  const entity = property.entity || property.itemEntity;
  if (!entity) {
    return false;
  }

  return entity.properties.every(
    (childProperty) =>
      !isValidApiExplorerProperty(childProperty, { depth: depth + 1 })
  );
}

/**
 * Returns true is the property is valid for the API Explorer, false otherwise
 * @param property The property to validate
 * @returns True if valid, false otherwise
 */
function isValidApiExplorerProperty(
  property: Property,
  options?: SortAndFilterOptions & { depth?: number }
): boolean {
  // If we are filtering based on a type field, and the property is a _data field, and it's not specifically
  // the `type_data` field, filter it out
  if (
    options?.typeFieldValue &&
    isAnyTypeDataField(property.name) &&
    !isTypeDataFieldForType(property.name, options.typeFieldValue)
  ) {
    return false;
  }

  return (
    !property.isRetired &&
    !property.isDeprecated &&
    !property.readOnly &&
    !isApiExplorerEntityPropertyEmpty(property, options?.depth || 0)
  );
}

const orderedPropertyNames = ['id', 'idempotency_key', 'type'];

/**
 * Sort it in a human-friendly way:
 * 1. ids are always first
 * 2. idempotency keys may come next
 * 3. type fields can come next for things like Catalog
 * 4. The rest is sorted alphabetically
 * @param a The property to compare
 * @param b The other property to compare
 * @returns -1 if a should come first, 1 if b should come first, 0 if they are equal
 */
function compareApiExplorerProperties(a: Property, b: Property): number {
  for (const name of orderedPropertyNames) {
    if (a.name === name) {
      return -1;
    } else if (b.name === name) {
      return 1;
    }
  }

  if (a.required && !b.required) {
    return -1;
  } else if (!a.required && b.required) {
    return 1;
  } else {
    return a.name.localeCompare(b.name);
  }
}

/**
 * For a given list of properties, filter out the invalid ones and sort them
 * for presentation in the API Explorer
 * @param properties The properties to filter and sort
 * @returns A new list of properties, filtered and sorted for the API Explorer
 */
function filterAndSortApiExplorerProperties(
  properties: Property[],
  options?: SortAndFilterOptions
): Property[] {
  // First, validate there's actually a field that ends with ${type}_data if set. If not,
  // don't filter.

  const filterOptions = { ...options };

  if (options?.typeFieldValue) {
    const hasTypeDataField = properties.some((property) =>
      isTypeDataFieldForType(property.name, options.typeFieldValue as string)
    );

    if (!hasTypeDataField) {
      filterOptions.typeFieldValue = undefined;
    }
  }

  return properties
    .filter((item) => isValidApiExplorerProperty(item, filterOptions))
    .sort(compareApiExplorerProperties);
}

export {
  isValidApiExplorerProperty,
  compareApiExplorerProperties,
  filterAndSortApiExplorerProperties,
  isAnyTypeDataField,
  isTypeDataFieldForType,
};
